// Converte tempo "HH:MM:SS" em segundos
const converterParaSegundos = (tempo) => {
  if (!tempo) return null;
  const [horas, minutos, segundos] = tempo.split(":").map(Number);
  return horas * 3600 + minutos * 60 + segundos;
};

// Converte segundos de volta para "HH:MM:SS"
const converterParaHHMMSS = (totalSegundos) => {
  if (totalSegundos == null || Number.isNaN(totalSegundos)) return null;
  const positivo = Math.abs(Math.round(totalSegundos));
  const horas = Math.floor(positivo / 3600);
  const minutos = Math.floor((positivo % 3600) / 60);
  const segundos = positivo % 60;
  const doisDigitos = (n) => String(n).padStart(2, "0");
  return `${doisDigitos(horas)}:${doisDigitos(minutos)}:${doisDigitos(segundos)}`;
};

// Calcula mínimo, média e máximo de uma lista de tempos em segundos
const calcularEstatisticas = (listaSegundos) => {
  if (!listaSegundos.length) {
    return { minimo: null, media: null, maximo: null };
  }

  const menorTempo = Math.min(...listaSegundos);
  const maiorTempo = Math.max(...listaSegundos);
  const soma = listaSegundos.reduce((acc, v) => acc + v, 0);
  const media = soma / listaSegundos.length;

  return {
    minimo: converterParaHHMMSS(menorTempo),
    media: converterParaHHMMSS(media),
    maximo: converterParaHHMMSS(maiorTempo),
    _mediaSegundos: media // guardo para cálculo do delta
  };
};

// Calcula percentual de variação entre médias (delta)
const calcularDelta = (mediaAtualSeg, mediaAnteriorSeg) => {
  if (
    mediaAtualSeg == null ||
    mediaAnteriorSeg == null ||
    !Number.isFinite(mediaAtualSeg) ||
    !Number.isFinite(mediaAnteriorSeg) ||
    mediaAnteriorSeg === 0
  ) {
    return null;
  }
  const pct = ((mediaAtualSeg - mediaAnteriorSeg) / mediaAnteriorSeg) * 100;
  return Math.round(pct * 10) / 10;
};


/**
 * Extrai uma lista de tempos (em segundos) do campo informado
 * @param {Array<object>} pedidos
 * @param {string} campo - ex.: "tempoConfirmacao" | "tempoProducao" | ...
 * @returns {number[]} tempos em segundos (somente valores válidos)
 */
const extrairTemposPorMes = (pedidos, campo) => {
  if (!Array.isArray(pedidos) || !campo) return [];

  return pedidos
    .map((p) => p?.[campo])
    //.filter((v) => typeof v === "string" && v.includes(":")) // garante formato "HH:MM[:SS]"
    .map((v) => converterParaSegundos(v))
  //.filter((v) => Number.isFinite(v)); // remove null/NaN
};


/**
 * Função principal
 * @param {Array} pedidos
 * @returns Objeto com totalizadores de tempo
 * @example const totalizadores = gerarTotalizadores(listaPedidos); 
 * console.log(JSON.stringify(totalizadores, null, 2));
 */
export function summaryTimeKPI(kpiPerformanceTimeBySatus) {
  const {
    currentMonthSales = [],  // registros do mês atual
    lastMonthSales = [],     // registros do mês anterior
    listAll = [],            // todos os registros
    monthSales = [],         // [{ month, records }]
    currentMonth             // número do mês atual (1..12)
  } = kpiPerformanceTimeBySatus || {};

  const base = { mesAtual: {}, mesAnterior: {}, outrosMeses: [], delta: null };

  if (!Array.isArray(listAll) || listAll.length === 0) {
    return {
      tempoConfirmacao: { ...base },
      tempoProducao: { ...base },
      tempoEntrega: { ...base },
      tempoTotal: { ...base }
    };
  }

  const campos = ["tempoConfirmacao", "tempoProducao", "tempoEntrega", "tempoTotal"];
  const resultado = {};

  for (const campo of campos) {
    // Mês atual
    const temposMesAtual = extrairTemposPorMes(currentMonthSales || [], campo);
    const estatisticasAtual = calcularEstatisticas(temposMesAtual);

    // Mês anterior
    const temposMesAnterior = extrairTemposPorMes(lastMonthSales || [], campo);
    const estatisticasAnterior = calcularEstatisticas(temposMesAnterior);

    // Outros meses (todos, exceto mês atual e mês anterior)
    const outrosMesesEstatisticas = [];

    if (Array.isArray(monthSales) && monthSales.length > 0 && typeof currentMonth === "number") {
      for (const mesInfo of monthSales) {
        const { month, year, records } = mesInfo || {};

        if (!month || !Array.isArray(records)) continue;

        // // ignora mês atual e mês anterior
        // if (month === currentMonth || (mesAnteriorNumero && month === mesAnteriorNumero)) {
        //   continue;
        // }

        const temposOutros = extrairTemposPorMes(records, campo);
        const estatOutros = calcularEstatisticas(temposOutros);

        outrosMesesEstatisticas.push({
          mes: month,
          ano: year,
          minimo: estatOutros.minimo,
          media: estatOutros.media,
          maximo: estatOutros.maximo
        });
      }
    }

    const delta = calcularDelta(
      estatisticasAtual._mediaSegundos,
      estatisticasAnterior._mediaSegundos
    );

    resultado[campo] = {
      mesAtual: {
        minimo: estatisticasAtual.minimo,
        media: estatisticasAtual.media,
        maximo: estatisticasAtual.maximo
      },
      mesAnterior: {
        minimo: estatisticasAnterior.minimo,
        media: estatisticasAnterior.media,
        maximo: estatisticasAnterior.maximo
      },
      outrosMeses: outrosMesesEstatisticas,
      delta
    };
  }


  return resultado;
}

/**
 * Gera KPIs de tempos POR UNIDADE a partir do estado kpiPerformanceTimeBySatus.
 * Mantém o mesmo cálculo de summaryTimeKPI (mesAtual x mesAnterior + delta),
 * porém agrupado por unidade.
 *
 * Retorno:
 * [
 *   {
 *     unidade: {
 *       idUnidade: string|number,
 *       unidadeName: string,
 *       tempos: {
 *         tempoConfirmacao: { mesAtual:{...}, mesAnterior:{...}, delta },
 *         tempoProducao:    { mesAtual:{...}, mesAnterior:{...}, delta },
 *         tempoEntrega:     { mesAtual:{...}, mesAnterior:{...}, delta },
 *         tempoTotal:       { mesAtual:{...}, mesAnterior:{...}, delta }
 *       }
 *     }
 *   }
 * ]
 */
export function listPerformanceTimeKPIByStore(kpiPerformanceTimeBySatus) {
  const {
    currentMonthSales = [],
    lastMonthSales = [],
    listAll = []
  } = kpiPerformanceTimeBySatus || {};

  // Sem base alguma, retorna lista vazia (mantendo semântica de "by unit")
  if (!Array.isArray(listAll) || listAll.length === 0) {
    return [];
  }

  // Descobre as unidades a partir de TODOS os registros (garante catálogo completo)
  // (poderíamos usar só currentMonthSales, mas listAll evita perder unidade sem mês atual)
  const unidadesMap = new Map();
  for (const r of listAll) {
    const key = r.unidadeName;
    if (!unidadesMap.has(key)) {
      unidadesMap.set(key, { idUnidade: r.idUnidade, unidadeName: r.unidadeName });
    }
  }

  const campos = ["tempoConfirmacao", "tempoProducao", "tempoEntrega", "tempoTotal"];
  const saida = [];

  // Para cada unidade, filtra mês atual e mês anterior e calcula KPIs
  for (const [, unidadeInfo] of unidadesMap.entries()) {
    const { idUnidade, unidadeName } = unidadeInfo;

    const atualDaUnidade = (currentMonthSales || []).filter(
      (r) => r.unidadeName === unidadeName
    );
    const anteriorDaUnidade = (lastMonthSales || []).filter(
      (r) => r.unidadeName === unidadeName
    );

    const tempos = {};

    for (const campo of campos) {
      const temposMesAtual = extrairTemposPorMes(atualDaUnidade, campo);
      const temposMesAnterior = extrairTemposPorMes(anteriorDaUnidade, campo);

      const estatisticasAtual = calcularEstatisticas(temposMesAtual);
      const estatisticasAnterior = calcularEstatisticas(temposMesAnterior);

      const delta = calcularDelta(
        estatisticasAtual._mediaSegundos,
        estatisticasAnterior._mediaSegundos
      );

      tempos[campo] = {
        mesAtual: {
          minimo: estatisticasAtual.minimo,
          media: estatisticasAtual.media,
          maximo: estatisticasAtual.maximo
        },
        mesAnterior: {
          minimo: estatisticasAnterior.minimo,
          media: estatisticasAnterior.media,
          maximo: estatisticasAnterior.maximo
        },
        delta
      };
    }

    saida.push({
      unidade: {
        idUnidade,
        unidadeName,
        tempos,
        pedidosMesAtual: atualDaUnidade,
        pedidosMesAnterior: anteriorDaUnidade
      }
    });
  }

  // (opcional) ordenar alfabeticamente por unidadeName
  saida.sort((a, b) => a.unidade.idUnidade.localeCompare(b.unidade.idUnidade));

  return saida;
}

